import AuditLogsTable from "./components/AuditLogs";
import { useEffect, useState, useContext } from "react";
import toast from "react-hot-toast";
import { ClientContext } from "ClientProvider";
import { useLocation } from "react-router-dom";
import { getAuditLogs } from "api/users";
import { getAllUsersList } from "api/graphQL_users";
import moment from "moment";

export interface LogsRowObj {
  id: any;
  user_id: any;
  module: any;
  description: any;
  ip_address: any;
  user_agent: any;
  created_at: any;
  updated_at: any;
  user_name: any;
}

const AuditLogs = () => {
  const [page, setPage] = useState<number>(1);
  const [logs, setLogs] = useState<LogsRowObj[]>([]);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { singleRoleData } = useContext(ClientContext);
  const [filter, setFilter] = useState({ dates: null, users: "" });
  const location = useLocation();
  const [localFilters, setLocalFilters] = useState<any>({});
  const [clearClicked, setClearClick] = useState(0);
  const [users, setUsers] = useState();

  const onClearClick = () => {
    setPage(1);
    setPageSize(50);
    setClearClick((prev) => prev + 1);
  };

  useEffect(() => {
    setPage(1);
    setPageSize(50);
  }, [searchTexts]);

  const fetchLogs = async () => {
    let finalSearchText = "";
    if (location.state?.search) {
      finalSearchText = location.state.search;
      window.history.replaceState({}, document.title);
    } else {
      finalSearchText = searchTexts;
    }

    const start_date = filter?.dates?.[0];
    const end_date = filter?.dates?.[1];

    if (pageSize === null || pageSize === undefined) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    let date_range = "";
    if (start_date && end_date) {
      date_range =
        moment(start_date).format("YYYY-MM-DD") +
        " to " +
        moment(end_date).format("YYYY-MM-DD");
    }

    const params = {
      page,
      per_page: pageSize,
      searchTexts: finalSearchText.trim(),
      start_date,
      end_date,
      user_id: filter?.users || "",
      date_range,
    };

    getAuditLogs(params)
      .then((data: any) => {
        setLogs(data?.user_logs);
        setTotalPage(data?.total_pages);
        setTotalItems(data?.total_records);
        setCurrentPage(data?.page);
      })
      .catch(() => {
        setLogs([]);
      })
      .finally(() => setIsLoading(false));
  };

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt);
      setPage(1);
      setCurrentPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize, searchTexts, clearClicked]);

  useEffect(() => {
    fetchUsers();
    setFilter({ dates: null, users: "" });
  }, []);

  const fetchUsers = () => {
    setIsLoading(true);
    const params = { page: 1, per_page: 5000, searchTexts: "" };
    getAllUsersList(params)
      .then((data) => setUsers(data.users))
      .catch((err) =>
        toast.error(
          err?.response?.data?.message ?? "Something went wrong while fetching users"
        )
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="al-root">
      {/* Page header */}
      <div className="al-page-header">
        <div className="al-page-title-block">
          <div className="al-page-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div>
            <h1 className="al-page-heading">Audit Logs</h1>
            <p className="al-page-sub">Track user activity and system events</p>
          </div>
        </div>
      </div>

      <AuditLogsTable
        users={users}
        tableData={logs}
        fetchLogs={fetchLogs}
        isLoading={isLoading}
        roleData={singleRoleData}
        onValueChange={serchboxValueChange}
        page={page}
        setPage={setPage}
        totalpage={totalpage}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setLocalFilters={setLocalFilters}
        filterData={fetchLogs}
        localFilters={localFilters}
        filter={filter}
        setFilter={setFilter}
        setClearClick={onClearClick}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .al-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .al-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }
        .al-page-title-block { display: flex; align-items: center; gap: 14px; }
        .al-page-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #faf5ff, #ede9fe);
          border: 1.5px solid #ddd6fe;
          display: flex; align-items: center; justify-content: center;
          color: #7c3aed; flex-shrink: 0;
        }
        .al-page-heading {
          font-size: 20px; font-weight: 800; color: #0f172a;
          letter-spacing: -0.4px; margin-bottom: 2px; line-height: 22px;
        }
        .al-page-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }

        @media (max-width: 600px) {
          .al-root { padding: 14px; gap: 14px; }
          .al-page-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default AuditLogs;